import type { CustomerKpiValue } from "@acto/taskpane/core/domain/Customer";
import { Kpi } from "@acto/taskpane/core/domain";
import { formatKpiValue, formatProfitWithMargin } from "@acto/taskpane/field-sales-2/utils/format";
import { useNumberFormatter } from "@acto/taskpane/core/utils/hooks/useNumberFormatter.tsx";
import type { ProductTrendItem } from "@acto/taskpane/field-sales-2/components/primitives/ProductTrendList";
import { CurrencyCode } from "@acto/taskpane/core/utils/currencyCode";

export interface CustomerTrendSource {
  id: string;
  name: string;
  externalReference: string | null;
  revenueKpi?: CustomerKpiValue;
  profitKpi?: CustomerKpiValue;
  ordersKpi?: CustomerKpiValue;
  marginKpi?: CustomerKpiValue;
}

/**
 * Maps a ranked customer to the shared product-trend row shape so the analysis "Kunden" breakdown
 * reuses the same list primitive as "Produktgruppen". The row value follows the selected KPI (Marge
 * shows profit with its margin, matching the customer cards); the row opens the customer's details
 * (no drill-down), so `hasChildren` is false but `canOpen` is true.
 */
export function toCustomerTrendItem(
  customer: CustomerTrendSource,
  selectedKpi: Kpi,
  locale: string,
  currency: CurrencyCode,
  formatNumber: ReturnType<typeof useNumberFormatter>,
): ProductTrendItem {
  const kpi = kpiFor(customer, selectedKpi);
  const value = kpi?.totalValue ?? null;
  const change = kpi?.percentageChange ?? null;

  const formattedValue =
    value == null
      ? null
      : selectedKpi === Kpi.MARGIN
        ? formatProfitWithMargin(value, customer.marginKpi?.totalValue ?? null, currency, locale)
        : formatKpiValue(selectedKpi, value, currency, locale);

  return {
    id: customer.id,
    name: customer.name,
    externalReference: customer.externalReference,
    hasChildren: false,
    canOpen: true,
    formattedValue,
    value: value ?? 0,
    change,
    formattedChange: change != null ? formatNumber({ number: change, type: "percentage", addPlusSign: true }) : null,
  };
}

function kpiFor(customer: CustomerTrendSource, selectedKpi: Kpi): CustomerKpiValue | undefined {
  switch (selectedKpi) {
    case Kpi.PROFIT:
    case Kpi.MARGIN:
      return customer.profitKpi;
    case Kpi.ORDERS:
      return customer.ordersKpi;
    default:
      return customer.revenueKpi;
  }
}
